import { __spreadArrays, __assign } from './_virtual/_tslib.js';
import { from, EMPTY, throwError } from 'rxjs';
import { mergeMap, map, reduce, switchMap, filter } from 'rxjs/operators';
import { SortMode, EventTypes } from './messaging-service-models.js';
import { removeUndefined, streamPages, convertConversationsResponseToPlatformConversations, getRecipientsFromConversationId, convertConversationResponseToPlatformConversation, convertMessageResponseToPlatformMessage, getConversationIdFromRecipients, convertMessageToPlatformMessage, convertConversationToPlatformConversation, platformEventMapper, MESSAGING_MESSAGE_TYPE } from './utils.js';

var MessagingAdapter = /** @class */ (function () {
    function MessagingAdapter(service, ownerSmsPhoneNumbers, base64Encoder) {
        if (base64Encoder === void 0) { base64Encoder = function (content) { return Promise.resolve(content); }; }
        this.service = service;
        this.base64Encoder = base64Encoder;
        this.type = MESSAGING_MESSAGE_TYPE;
        this.ownerSmsPhoneNumbers =
            typeof ownerSmsPhoneNumbers == 'function' ? ownerSmsPhoneNumbers : function () { return ownerSmsPhoneNumbers; };
    }
    /**
     * @param count number of conversations to be returned
     * @param pageIdentifiers paginated identifier
     * @returns all the conversations in which the recipient is involved for this provider
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.getConversations = function (count, pageIdentifiers, folder) {
        var _this = this;
        return from(this.ownerSmsPhoneNumbers()).pipe(mergeMap(function (ownerPhoneNumber) {
            var _a;
            var params = removeUndefined({
                ownerPhoneNumber: ownerPhoneNumber,
                sort: SortMode.NEWEST_MESSAGE,
                folder: folder,
            });
            var phoneNumberPageIdentifier = (_a = pageIdentifiers) === null || _a === void 0 ? void 0 : _a[ownerPhoneNumber];
            if (pageIdentifiers && !phoneNumberPageIdentifier) {
                return EMPTY;
            }
            return streamPages(params, count, phoneNumberPageIdentifier, _this.service.getConversations).pipe(map(function (item) { return convertConversationsResponseToPlatformConversations(ownerPhoneNumber, item); }));
        }), reduce(function (accumulator, pagedConversations) { return ({
            conversations: __spreadArrays(accumulator.conversations, pagedConversations.conversations),
            pageIdentifiers: __assign(__assign({}, accumulator.pageIdentifiers), pagedConversations.pageIdentifiers),
        }); }));
    };
    /**
     * @param conversationId id of the conversation
     * @returns conversation for a conversationId
     */
    MessagingAdapter.prototype.getConversation = function (conversationId) {
        var _a = getRecipientsFromConversationId(conversationId), ownerPhoneNumber = _a.ownerPhoneNumber, contactPhoneNumbers = _a.contactPhoneNumbers;
        return this.service
            .getConversations({
            contactPhoneNumbers: contactPhoneNumbers,
            ownerPhoneNumber: ownerPhoneNumber,
        })
            .pipe(map(function (item) { return convertConversationResponseToPlatformConversation(ownerPhoneNumber, item); }));
    };
    /**
     * SortBy.REVERSE + range = [0, 49] means the last 50 messages
     * @param conversationId unique identifier to represent a conversation `ownerPhoneNumber|contactPhoneNumbeer`
     * @param sortBy OLDEST_FIRST or NEWEST_FIRST (by default)
     * @param count number of conversations to be returned
     * @param pageIdentifier paginated identifier
     * @returns the messages of a specific conversation
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.getConversationMessages = function (conversationId, count, pageIdentifier) {
        var _a = getRecipientsFromConversationId(conversationId), ownerPhoneNumber = _a.ownerPhoneNumber, contactPhoneNumbers = _a.contactPhoneNumbers;
        var params = {
            contactPhoneNumbers: contactPhoneNumbers,
            ownerPhoneNumber: ownerPhoneNumber,
        };
        return streamPages(params, count, pageIdentifier, this.service.getMessages).pipe(map(convertMessageResponseToPlatformMessage));
    };
    /**
     * Sends an message to an array of recipients
     * @param recipients the recipients identifying the conversation
     * @param body the content of the message
     * @returns message sent
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.send = function (recipients, _a, correlationId) {
        var _this = this;
        var body = _a.body, media = _a.media;
        var owner = recipients[0], contact = recipients[1];
        return from(this.base64Encoder(media)).pipe(switchMap(function (base64Media) {
            return _this.service
                .sendMessage({
                body: body,
                contactPhoneNumbers: [contact.id],
                media: base64Media,
                ownerPhoneNumber: owner.id,
                tag: correlationId,
            })
                .pipe(map(function (response) { return ({
                conversationId: getConversationIdFromRecipients(response.ownerPhoneNumber, response.contactPhoneNumbers),
                message: convertMessageToPlatformMessage(response),
                recipients: response.contactPhoneNumbers.map(function (id) { return ({ id: id }); }),
            }); }));
        }));
    };
    /**
     * @returns all the unread of any conversation in which the recipient is involved
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.getAllUnreadMessages = function () {
        var _this = this;
        return from(this.ownerSmsPhoneNumbers()).pipe(mergeMap(function (ownerPhoneNumber) {
            var conversationsParams = {
                ownerPhoneNumber: ownerPhoneNumber,
                unreadOnly: true,
            };
            return streamPages(conversationsParams, Number.MAX_VALUE, undefined, _this.service.getConversations).pipe(filter(function (_a) {
                var items = _a.items;
                return !!items.length;
            }), switchMap(function (_a) {
                var items = _a.items;
                return from(items).pipe(mergeMap(function (_a) {
                    var contactPhoneNumbers = _a.contactPhoneNumbers, unreadMessagesCount = _a.unreadMessagesCount, lastReadMessageTimestamp = _a.lastReadMessageTimestamp;
                    var messagesParams = {
                        contactPhoneNumbers: contactPhoneNumbers,
                        ownerPhoneNumber: ownerPhoneNumber,
                    };
                    if (lastReadMessageTimestamp) {
                        messagesParams = __assign(__assign({}, messagesParams), { oldestTimestamp: lastReadMessageTimestamp });
                    }
                    return streamPages(messagesParams, unreadMessagesCount, undefined, _this.service.getMessages);
                }), map(convertMessageResponseToPlatformMessage));
            }));
        }), map(function (pagedMessages) { return pagedMessages.results; }), reduce(function (accumulator, messages) { return __spreadArrays(accumulator, messages); }));
    };
    /**
     * @returns a count of all the unread messages from all the conversations
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.getAllUnreadMessagesCount = function () {
        var _this = this;
        return from(this.ownerSmsPhoneNumbers()).pipe(mergeMap(function (ownerPhoneNumber) {
            return _this.service
                .getConversationsSummary({ ownerPhoneNumber: ownerPhoneNumber })
                .pipe(map(function (_a) {
                var unreadMessagesCount = _a.unreadMessagesCount;
                return unreadMessagesCount;
            }));
        }), reduce(function (accumulator, unreadMessagesCount) { return accumulator + unreadMessagesCount; }, 0));
    };
    /**
     * @param messageId unique identifier to represent a message
     * @returns message with an empty body
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.deleteMessage = function (messageId) {
        return this.service.deleteMessage({ messageId: messageId }).pipe(map(convertMessageToPlatformMessage));
    };
    /**
     * @param conversationId unique identifier to represent a conversation
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.deleteConversation = function (conversationId) {
        var _a = getRecipientsFromConversationId(conversationId), ownerPhoneNumber = _a.ownerPhoneNumber, contactPhoneNumbers = _a.contactPhoneNumbers;
        return this.service.deleteConversation({ ownerPhoneNumber: ownerPhoneNumber, contactPhoneNumbers: contactPhoneNumbers });
    };
    /**
     * @param conversationId unique identifier to represent a conversation
     */
    MessagingAdapter.prototype.createConversation = function (_conversationId) {
        return throwError("'createConversation' unsupported for SMS/MMS");
    };
    /**
     * @param conversationId unique identifier to represent a conversation
     * @param members members to be added to a group chat
     */
    MessagingAdapter.prototype.addMembers = function (_conversationId, _members) {
        return throwError("'addMembers' unsupported for SMS/MMS");
    };
    /**
     * @param messageId unique identifier to represent a message
     * @param conversationId unique identifier to represent a conversation
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.markConversationAsRead = function (messageId, conversationId) {
        var _a = getRecipientsFromConversationId(conversationId), ownerPhoneNumber = _a.ownerPhoneNumber, contactPhoneNumbers = _a.contactPhoneNumbers;
        return this.service
            .updateConversation({
            contactPhoneNumbers: contactPhoneNumbers,
            lastReadMessageId: messageId,
            ownerPhoneNumber: ownerPhoneNumber,
        })
            .pipe(map(convertConversationToPlatformConversation));
    };
    /**
     * @param conversationId unique identifier to represent a conversation
     * @param folder represents a folder where the conversation will be allocated
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.updateConversationFolder = function (conversationId, folder) {
        var _a = getRecipientsFromConversationId(conversationId), ownerPhoneNumber = _a.ownerPhoneNumber, contactPhoneNumbers = _a.contactPhoneNumbers;
        return this.service
            .updateConversation({
            contactPhoneNumbers: contactPhoneNumbers,
            ownerPhoneNumber: ownerPhoneNumber,
            folder: folder,
        })
            .pipe(map(convertConversationToPlatformConversation));
    };
    /**
     * @param channelId unique identifier to represent a notification channel subscription
     * @param eventTypes an array of strings representing events for subscription
     * @param messagingResource a unique resource that can be observed for events
     * @returns created subscription
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.createSubscription = function (channelId, eventTypes, messagingResource) {
        var events = eventTypes.map(function (event) { return EventTypes[event]; });
        return this.service
            .subscribe({
            channelId: channelId,
            eventTypes: events,
            ownerPhoneNumber: messagingResource.id,
        })
            .pipe(map(function (subscription) { return subscription.id; }));
    };
    /**
     * @param events$ an observable that can be connected to for relevant events that will be provided by the notification channel
     * @returns mapped events
     */
    MessagingAdapter.prototype.registerEventSource = function (events$) {
        return events$.pipe(map(function (notificationChannelEvent) {
            return platformEventMapper(notificationChannelEvent);
        }), filter(function (notificationChannelEvent) {
            return !!notificationChannelEvent;
        }));
    };
    /**
     * @param subscriptionId unique identifier to represent a notification channel subscription
     * @throws if an error is thrown by the http call
     */
    MessagingAdapter.prototype.deleteSubscription = function (subscriptionId) {
        return this.service.deleteSubscription({ subscriptionId: subscriptionId });
    };
    MessagingAdapter.prototype.getSendMessageCompleteLogEvent = function (metadata) {
        return {
            eventName: 'messaging-platform-sms-send',
            metadata: metadata,
        };
    };
    return MessagingAdapter;
}());

export { MessagingAdapter };
